/* @jsx h */
import { defineComponent, h, ref } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import type { WhatsAppButton, WhatsAppContent, WhatsAppHeader } from "../types";
import { newId, placeholderImage } from "../stub";
import { CharCounter, Field, SampleCustomerPicker, Segmented, TemplateTextarea } from "../shared";
import { WhatsAppPreview } from "./WhatsAppPreview";

type WhatsAppEditorProps = {
  content: WhatsAppContent;
  onChange: (content: WhatsAppContent) => void;
};

// Meta limits for marketing template messages.
const LIMITS = { body: 1024, header: 60, footer: 60, button: 25, buttons: 3, url: 2, copy_code: 1 };

const STATUS_BADGE: Record<WhatsAppContent["approval_status"], string> = {
  not_submitted: "badge-ghost",
  pending: "badge-warning",
  approved: "badge-success",
  rejected: "badge-error",
};

export const WhatsAppEditor = defineComponent<WhatsAppEditorProps>({
  name: "WhatsAppEditor",
  props: ["content", "onChange"],
  setup(props) {
    const { t } = useLocalization();
    const body = ref<{ wrap: (marker: string) => void }>();

    // Any content edit invalidates a previous Meta approval.
    const patch = (p: Partial<WhatsAppContent>) =>
      props.onChange({
        ...props.content,
        ...p,
        approval_status: "approval_status" in p ? p.approval_status! : "not_submitted",
      });

    const submitForApproval = () => {
      patch({ approval_status: "pending" });
      // Stub: Meta usually answers within minutes; fake it.
      setTimeout(() => {
        if (props.content.approval_status === "pending") {
          props.onChange({ ...props.content, approval_status: "approved" });
        }
      }, 2500);
    };

    const countOf = (type: WhatsAppButton["type"]) => props.content.buttons.filter((b) => b.type === type).length;
    const canAdd = (type: WhatsAppButton["type"]) =>
      props.content.buttons.length < LIMITS.buttons &&
      (type === "quick_reply" || countOf(type) < LIMITS[type]);

    const addButton = (type: WhatsAppButton["type"]) => {
      const id = newId();
      const button: WhatsAppButton =
        type === "url"
          ? { id, type, label: t("campaigns.studio_whatsapp.default_url_label"), url: "https://" }
          : type === "copy_code"
            ? { id, type, label: t("campaigns.studio_whatsapp.default_copy_label") }
            : { id, type, label: t("campaigns.studio_whatsapp.default_reply_label") };
      patch({ buttons: [...props.content.buttons, button] });
    };

    const updateButton = (button: WhatsAppButton) =>
      patch({ buttons: props.content.buttons.map((b) => (b.id === button.id ? button : b)) });

    const setHeaderType = (type: string) => {
      const header: WhatsAppHeader =
        type === "text"
          ? { type: "text", text: "{{shop_name}}" }
          : type === "image"
            ? { type: "image", src: placeholderImage("Café Lemmon", "#264653", "#2a9d8f") }
            : { type: "none" };
      patch({ header });
    };

    const Approval = () => {
      const status = props.content.approval_status;
      return (
        <div class="flex items-start gap-3 rounded-xl border border-base-300 bg-base-100 p-3">
          <span class="material-symbols-outlined mt-0.5 text-success">verified_user</span>
          <div class="min-w-0 flex-1 text-sm">
            <div class="flex flex-wrap items-center gap-2">
              <span class="font-semibold">{t("campaigns.studio_whatsapp.template_approval")}</span>
              <span class={["badge badge-sm", STATUS_BADGE[status]]}>{t(`campaigns.studio_whatsapp.status_${status}`)}</span>
              <span class="badge badge-outline badge-sm">{t("campaigns.studio_whatsapp.category_marketing")}</span>
            </div>
            <p class="mt-1 text-xs text-base-content/60">{t("campaigns.studio_whatsapp.approval_hint")}</p>
          </div>
          {(status === "not_submitted" || status === "rejected") && (
            <button type="button" class="btn btn-primary btn-sm" onClick={submitForApproval}>
              {t("campaigns.studio_whatsapp.submit")}
            </button>
          )}
          {status === "pending" && <span class="loading loading-spinner loading-sm text-warning" />}
        </div>
      );
    };

    const FormatButton = (marker: string, icon: string) => (
      <button
        type="button"
        class="btn btn-ghost btn-xs btn-square"
        onMousedown={(e) => e.preventDefault()}
        onClick={() => body.value?.wrap(marker)}
      >
        <span class="material-symbols-outlined text-base">{icon}</span>
      </button>
    );

    return () => {
      const c = props.content;
      return (
        <div class="flex h-full min-h-0 overflow-hidden">
          <div class="min-w-0 flex-1 space-y-4 overflow-y-auto p-6">
            {Approval()}

            <div class="grid grid-cols-2 gap-3">
              <Field label={t("campaigns.studio_whatsapp.template_name")} hint={t("campaigns.studio_whatsapp.template_name_hint")}>
                <input
                  type="text"
                  class="input input-bordered input-sm w-full font-mono"
                  value={c.template_name}
                  onInput={(e) =>
                    patch({ template_name: (e.target as HTMLInputElement).value.toLowerCase().replace(/[^a-z0-9_]/g, "_") })
                  }
                />
              </Field>
              <Field label={t("campaigns.studio_whatsapp.language")}>
                <select
                  class="select select-bordered select-sm w-full"
                  value={c.language}
                  onChange={(e) => patch({ language: (e.target as HTMLSelectElement).value })}
                >
                  <option value="de">Deutsch</option>
                  <option value="fr">Français</option>
                  <option value="it">Italiano</option>
                  <option value="en">English</option>
                </select>
              </Field>
            </div>

            <Field label={t("campaigns.studio_whatsapp.header")}>
              <Segmented
                value={c.header.type}
                options={[
                  { value: "none", label: t("campaigns.studio_whatsapp.header_none") },
                  { value: "text", label: t("campaigns.studio_whatsapp.header_text"), icon: "title" },
                  { value: "image", label: t("campaigns.studio_whatsapp.header_image"), icon: "image" },
                ]}
                onChange={setHeaderType}
              />
            </Field>
            {c.header.type === "text" && (
              <Field label={t("campaigns.studio_whatsapp.header_text")} counter={CharCounter(c.header.text.length, LIMITS.header)}>
                <input
                  type="text"
                  class="input input-bordered input-sm w-full"
                  maxlength={LIMITS.header}
                  value={c.header.text}
                  onInput={(e) => patch({ header: { type: "text", text: (e.target as HTMLInputElement).value } })}
                />
              </Field>
            )}

            <Field
              label={t("campaigns.studio_whatsapp.body")}
              counter={CharCounter(c.body.length, LIMITS.body)}
              error={c.body.length > LIMITS.body}
            >
              <TemplateTextarea
                ref={body}
                channel="whatsapp"
                rows={8}
                maxLength={LIMITS.body}
                value={c.body}
                onChange={(v) => patch({ body: v })}
                toolbar={() => (
                  <span class="flex gap-1">
                    {FormatButton("*", "format_bold")}
                    {FormatButton("_", "format_italic")}
                    {FormatButton("~", "format_strikethrough")}
                    {FormatButton("```", "code")}
                  </span>
                )}
              />
            </Field>

            <Field label={t("campaigns.studio_whatsapp.footer")} counter={CharCounter(c.footer.length, LIMITS.footer)}>
              <input
                type="text"
                class="input input-bordered input-sm w-full"
                maxlength={LIMITS.footer}
                value={c.footer}
                onInput={(e) => patch({ footer: (e.target as HTMLInputElement).value })}
              />
            </Field>

            <div>
              <div class="mb-2 flex items-center justify-between">
                <span class="text-sm font-medium">
                  {t("campaigns.studio_whatsapp.buttons")}
                  <span class="ml-2 text-xs text-base-content/50">{CharCounter(c.buttons.length, LIMITS.buttons)}</span>
                </span>
                <div class="dropdown dropdown-end">
                  <button type="button" tabindex={0} class="btn btn-ghost btn-xs gap-1" disabled={c.buttons.length >= LIMITS.buttons}>
                    <span class="material-symbols-outlined text-base">add</span>
                    {t("campaigns.studio_whatsapp.add_button")}
                  </button>
                  <ul tabindex={0} class="menu dropdown-content z-30 w-52 rounded-box bg-base-100 p-2 shadow">
                    {(["copy_code", "url", "quick_reply"] as const).map((type) => (
                      <li key={type} class={!canAdd(type) ? "disabled" : ""}>
                        <button type="button" disabled={!canAdd(type)} onClick={() => addButton(type)}>
                          {t(`campaigns.studio_whatsapp.button_${type}`)}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <div class="space-y-2">
                {c.buttons.map((b) => (
                  <div key={b.id} class="flex items-center gap-2 rounded-lg border border-base-300 p-2">
                    <span class="badge badge-ghost badge-sm shrink-0">{t(`campaigns.studio_whatsapp.button_${b.type}`)}</span>
                    <input
                      type="text"
                      class="input input-bordered input-xs w-32"
                      maxlength={LIMITS.button}
                      value={b.label}
                      onInput={(e) => updateButton({ ...b, label: (e.target as HTMLInputElement).value })}
                    />
                    {b.type === "url" && (
                      <input
                        type="url"
                        class="input input-bordered input-xs min-w-0 flex-1"
                        value={b.url}
                        onInput={(e) => updateButton({ ...b, url: (e.target as HTMLInputElement).value })}
                      />
                    )}
                    <button
                      type="button"
                      class="btn btn-ghost btn-xs btn-square ml-auto"
                      onClick={() => patch({ buttons: c.buttons.filter((x) => x.id !== b.id) })}
                    >
                      <span class="material-symbols-outlined text-base">delete</span>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div class="flex w-[380px] shrink-0 flex-col items-center gap-4 overflow-y-auto border-l border-base-300 bg-base-200 p-6">
            <SampleCustomerPicker />
            <WhatsAppPreview content={c} />
          </div>
        </div>
      );
    };
  },
});
